import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Constant from "@tSurya/Constants/Constant";
import { MGBW_IS_CONFIGURABLE, MGBW_COL, MGBW_COM } from "@tSurya/Common/MgbwPropertyKeys";
import {
    ConfigurationDataResultModel,
    ConfiguratorProductModel,
    CylindoConfigModel,
    PreConfiguredStyleModel,
    ProductOptionModel,
    ProductOptionValueModel,
    SelectionImageModel,
} from "@tSurya/Store/Data/ConfiguratorModels";
import CustomApplicationState from "@tSurya/Store/Data/CustomApplicationState/CustomApplicationState";
import { ConfiguratorState } from "@tSurya/Store/Data/ConfiguratorState";
import { resolveSelectionImages } from "@tSurya/Common/Utilities/selectionImageResolver";
import { createSelector } from "reselect";

export function getConfiguratorState(state: CustomApplicationState): ConfiguratorState {
    return state.SURData.configuratorStore;
}

export function isConfiguratorProduct(product: ProductModel): boolean {
    return product.properties[MGBW_IS_CONFIGURABLE] === "true";
}

export function getConfiguratorSelections(state: CustomApplicationState): Record<string, string> {
    return state.SURData?.configuratorStore?.selections;
}

export function getCylindoConfig(state: CustomApplicationState): CylindoConfigModel | null {
    return state.SURData.configuratorStore.cylindoConfig;
}

const noUpcharges: Record<string, Record<string, number>> = {};

/**
 * Per-value upcharges of the currently resolved item, indexed by optionId then valueId.
 * Sourced from the configuration result rather than the product config, which is loaded
 * once per page and would keep showing the initial item's amounts.
 */
export const getOptionValueUpcharges = createSelector(
    [(state: CustomApplicationState) => state.SURData.configuratorStore.configurationResult?.optionValueUpcharges],
    (upcharges): Record<string, Record<string, number>> => {
        if (!upcharges?.length) {
            return noUpcharges;
        }

        const byOptionId: Record<string, Record<string, number>> = {};
        for (const upcharge of upcharges) {
            (byOptionId[upcharge.optionId] ??= {})[upcharge.valueId] = upcharge.amount;
        }
        return byOptionId;
    },
);

/**
 * Resolves the best-matching selection-driven image for the current selections, used as the
 * preview fallback when Cylindo is unavailable or cannot render the selection. Returns null
 * when no attribute image matches.
 */
export const getResolvedAttributeImages = createSelector(
    [
        (state: CustomApplicationState) => state.SURData.configuratorStore.productConfig?.selectionImages,
        (state: CustomApplicationState) => state.SURData.configuratorStore.productConfig?.options,
        (state: CustomApplicationState) => state.SURData.configuratorStore.selections,
    ],
    (selectionImages, options, selections): SelectionImageModel[] =>
        resolveSelectionImages(selections, options, selectionImages),
);

export const getResolvedAttributeImage = createSelector(
    [getResolvedAttributeImages],
    selectionImages => selectionImages[0] || null,
);

/** Option handles that always carry the cover appearance, in addition to options typed "cover". */
const coverOptionHandles = ["basecover", "contrastinout"];

/** True when the option drives the product's cover/upholstery appearance (base cover, contrast cover, ...). */
export function isCoverOption(option: ProductOptionModel): boolean {
    return (
        option.type?.toLowerCase() === "cover" ||
        coverOptionHandles.includes(option.optionHandle?.toLowerCase() ?? "")
    );
}

/**
 * Cylindo feature codes that were dropped during validation AND map to a cover-type option.
 * A dropped cover means the Cylindo render no longer reflects the chosen fabric, so the
 * selection image must be used instead.
 */
export const getUnsupportedCoverCylindoFeatures = createSelector(
    [
        (state: CustomApplicationState) => state.SURData.configuratorStore.droppedCylindoFeatures,
        (state: CustomApplicationState) => state.SURData.configuratorStore.productConfig?.options,
    ],
    (droppedCylindoFeatures, options): string[] => {
        if (!droppedCylindoFeatures?.length) {
            return [];
        }
        return droppedCylindoFeatures.filter(featureCode => {
            const option = options?.find(
                o => o.optionHandle && o.optionHandle.toLowerCase() === featureCode.toLowerCase(),
            );
            if (!option) {
                return false;
            }
            return isCoverOption(option);
        });
    },
);

/**
 * Mirrors the Cylindo viewer's fallback condition so the cart, wish list and order lines show
 * exactly the image the PDP rendered.
 */
export const getShouldUseSelectionImage = createSelector(
    [getUnsupportedCoverCylindoFeatures, getResolvedAttributeImage],
    (unsupportedCoverFeatures, attributeImage) => unsupportedCoverFeatures.length > 0 && attributeImage !== null,
);

export function getSelectedStyleId(state: CustomApplicationState): string | null {
    return state.SURData.configuratorStore.selectedStyleId;
}

/**
 * Resolves the customer-facing Item# for the current configuration. Returns the
 * selected pre-configured style id when one is active and matches a known style,
 * otherwise the resolved item id suffixed with `-CUSTOM`. Returns null when the
 * configuration has not been resolved yet.
 */
export function getDisplayItemId(
    productConfig: ConfiguratorProductModel | null,
    configurationResult: ConfigurationDataResultModel | null,
    selectedStyleId: string | null,
): string | null {
    if (!productConfig || !configurationResult) {
        return null;
    }

    if (selectedStyleId) {
        const matchingStyle = productConfig.preConfiguredStyles?.find(s => s.styleId === selectedStyleId);
        if (matchingStyle) {
            return matchingStyle.styleId;
        }
    }

    return `${configurationResult.resolvedItemId}-CUSTOM`;
}

/**
 * Finds the style whose selections match the given selections exactly.
 * Returns the matching style's styleId, or null if no match.
 */
export function findMatchingStyleId(
    styles: PreConfiguredStyleModel[],
    selections: Record<string, string>,
): string | null {
    var selectionKeys = Object.keys(selections);
    for (var style of styles) {
        var keys = Object.keys(style.selections);
        if (keys.length === 0) {
            continue;
        }
        if (keys.length !== selectionKeys.length) {
            continue;
        }
        var allMatch = keys.every(k => selections[k] === style.selections[k]);
        if (allMatch) {
            return style.styleId;
        }
    }
    return null;
}

export const getSelectedBaseCoverName = createSelector(
    [
        (state: CustomApplicationState) => state.SURData.configuratorStore.productConfig?.options,
        (state: CustomApplicationState) => state.SURData.configuratorStore.selections,
        (state: CustomApplicationState) => state.SURData.configuratorStore.valueLookup,
    ],
    (options, selections, valueLookup) => {
        if (!options) return null;
        const coverOption = options.find(o => o.type === "cover");
        if (!coverOption) return null;
        const coverValueId = selections[coverOption.optionId];
        if (!coverValueId) return null;
        return valueLookup?.[coverValueId] ?? null;
    },
);

export const getSelectedBaseCoverSwatchProductId = createSelector(
    [
        (state: CustomApplicationState) => state.SURData.configuratorStore.productConfig?.options,
        (state: CustomApplicationState) => state.SURData.configuratorStore.selections,
    ],
    (options, selections) => {
        if (!options) return null;
        const coverOption = options.find(o => o.type === "cover");
        if (!coverOption?.optionGroup) return null;
        const coverValueId = selections[coverOption.optionId];
        if (!coverValueId) return null;
        for (const entry of coverOption.optionGroup.entries) {
            const match = entry.values.find(v => v.valueId === coverValueId);
            if (match?.swatchProductId) return match.swatchProductId;
        }
        return null;
    },
);

export const getResolvedSelections = createSelector(
    [
        (state: CustomApplicationState) => state.SURData.configuratorStore.productConfig?.options,
        (state: CustomApplicationState) => state.SURData.configuratorStore.selections,
        (state: CustomApplicationState) => state.SURData.configuratorStore.valueLookup,
    ],
    (options, selections, valueLookup) => {
        if (!options) return [];
        return options
            .filter(o => o.name.toLowerCase() !== Constant.optionNamePackaging)
            .slice()
            .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
            .flatMap(o => {
                const valueId = selections[o.optionId];
                const valueName = valueId ? (valueLookup?.[valueId] ?? null) : null;
                return valueName ? [{ optionId: o.optionId, optionName: o.name, valueName }] : [];
            });
    },
);

/**
 * Resolves the option-dependent shipping availability for the current configurator selections.
 * Prefers the backend-calculated value on the latest configuration result; falls back to a
 * client-side scan of the selected option values ordered by option sequence (first non-empty wins).
 * Returns null when no selected option value carries a message (callers should then fall back
 * to the product-level mgbw_mtoShippingAvailability property).
 */
export const getResolvedShippingAvailability = createSelector(
    [
        (state: CustomApplicationState) => state.SURData.configuratorStore.configurationResult,
        (state: CustomApplicationState) => state.SURData.configuratorStore.productConfig?.options,
        (state: CustomApplicationState) => state.SURData.configuratorStore.selections,
    ],
    (configurationResult, options, selections) => {
        if (configurationResult?.shippingAvailability) {
            return configurationResult.shippingAvailability;
        }

        if (!options || !selections) {
            return null;
        }

        const sortedOptions = options.slice().sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
        for (const option of sortedOptions) {
            const valueId = selections[option.optionId];
            if (!valueId || !option.optionGroup) {
                continue;
            }
            for (const entry of option.optionGroup.entries) {
                const match = entry.values.find(v => v.valueId === valueId);
                if (match?.shippingAvailability) {
                    return match.shippingAvailability;
                }
            }
        }

        return null;
    },
);

/**
 * Returns the COM or COL approximate-requirements text from the current
 * configurator product's custom properties. Tries `mgbw_com` first, then
 * `mgbw_col`. Returns null when neither property exists.
 */
export const getComRequirementsText = createSelector(
    [
        (state: CustomApplicationState) => state.SURData.configuratorStore.productConfig?.productId,
        (state: CustomApplicationState) => state.data.products.byId,
    ],
    (productNumber, byId) => {
        if (!productNumber) {
            return null;
        }

        const product = Object.keys(byId)
            .map(id => byId[id])
            .find(p => p?.productNumber === productNumber);

        return product ? product.properties[MGBW_COM] || product.properties[MGBW_COL] || null : null;
    },
);
